import React from "react";
import { Avatar as MuiAvatar, Divider, Typography } from "@material-ui/core";
import dayjs from "~/config/dayjs";
import { useTranslation } from "react-i18next";
import Utils from "../../../helpers/Utils";
import useDateFormatter from "../../../hooks/useDateFormatter";
import useStyles from "./styles";
import timeIcon from "assets/icons/group-4.png";
import { useSelector } from "react-redux";
import { getInitials } from "~/utils/business";
import ServiceExpiredCard from "~/components/common/serviceExpiredCard";

// Wrapper nomeado para permitir que enzyme localize pelo nome "Avatar"
function Avatar(props) {
	return <MuiAvatar {...props} />;
}

const ServiceItem = ({
	service,
	index,
	totalServices,
	checkoutInformation,
	currency,
	viewUponConsultation,
	showDivider = true,
	showViewValues = true,
	isExpired = false,
	isUpdated = false,
	expiredCardProps,
	expiredTagLabel,
	priceLabel,
	priceStrikethrough = false,
}) => {
	const { business } = useSelector((store) => store.business);
	const classes = useStyles();
	const { t, i18n } = useTranslation("");
	const { formatTime, formatDateToLanguage } = useDateFormatter();
	const formatDateByLanguage = formatDateToLanguage();

	const pricingType = service?.professional?.pricing_type || service?.professional?.pricingType;
	let amount = service?.professional?.price ?? 0;
	if (service?.service?.servicePrice > amount) {
		amount = service?.service?.servicePrice;
	}
	const shouldStartAt = Utils.startsAtPricingTypeNames.includes(pricingType);
	const shouldShowUponConsultation = Utils.dontShowPricingTypeNames.includes(pricingType);
	const shouldShowFreePrice = Utils.freePricingTypeNames.includes(pricingType);

	const hasAmount =
		!Utils.freePricingTypeNames.includes(pricingType) &&
		!Utils.dontShowPricingTypeNames.includes(pricingType) &&
		amount !== undefined &&
		amount !== null;
	const amountText = hasAmount ? Utils.formatMoney((amount / 100), currency) : "";

	return (
		<div className={index < totalServices - 1 ? classes.serviceItem : classes.serviceItemLast}>
			<div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
				<div>
					<div className={classes.serviceHeader}>
						<Typography
							translate="no"
							className={classes.serviceName}
							style={{
								fontWeight: totalServices > 1 ? "normal" : "bold",
							}}>
							{service.service?.serviceName || service.service?.name}
						</Typography>
					</div>
					<div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
						<div>
							<Avatar
								variant="circle"
								src={service?.professional?.avatarUrl || ""}
								className={classes.headerAvatar}
								style={{ backgroundColor: "#EEEEEE", width: 48, height: 48, color: "#555555" }}>
								{!service?.professional?.avatarUrl && getInitials(service?.professional?.name || "")}
							</Avatar>
						</div>
						<div>
							<Typography className={classes.professionalName}>{service?.professional?.name}</Typography>
							<div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
								<Typography
									className={classes.serviceDate}
									style={isExpired ? { textDecoration: "line-through", color: "#9B928C" } : undefined}>
									{`${dayjs(service.dateStart || service.date).utc().tz(business?.timeZone).format(formatDateByLanguage?.dayMonth)} ${t(
										"at"
									)} ${formatTime(dayjs(service.dateStart || service.date).utc().tz(business?.timeZone).format("HH:mm"))}`}
								</Typography>
								{isExpired && (
									<span
										style={{
											fontSize: 11.5,
											fontWeight: 700,
											padding: "3px 8px",
											borderRadius: 7,
											color: "#672E37",
											background: "#F6E7E4",
										}}>
										{expiredTagLabel || t("expiredTag")}
									</span>
								)}
								{!isExpired && (
									<>
										<img src={timeIcon} alt="Horário" style={{ width: 12, height: 12, margin: "0 4px" }} />
										<Typography className={classes.serviceDuration}>
											{`${Utils.formatMinutes(service.professional?.duration || service.service?.serviceDuration || 60)}`}
										</Typography>
									</>
								)}
								{isUpdated && (
									<span
										style={{
											fontSize: 11.5,
											fontWeight: 700,
											padding: "3px 8px",
											borderRadius: 7,
											color: "#2F6B4F",
											background: "#E5F0E9",
										}}>
										{t("updatedTag")}
									</span>
								)}
							</div>
							{totalServices === 1 && (
									<>
										{shouldStartAt && viewUponConsultation && (
											<Typography className={classes.uponConsultation}>
												({t("Starts at")} {amountText})
											</Typography>
										)}
										{shouldShowUponConsultation && viewUponConsultation && (
											<Typography className={classes.uponConsultation}>
												({t("Upon consultation")})
											</Typography>
										)}
										{shouldShowFreePrice && viewUponConsultation && (
											<Typography className={classes.uponConsultation}>
												({t("Free")})
											</Typography>
										)}
									</>
								)}
						</div>
					</div>
				</div>
				{showViewValues ? (
					<div>
					{priceStrikethrough ? (
						<Typography translate="no" style={{ fontSize: 14, fontWeight: 600, textAlign: "right", color: "rgba(0,0,0,0.35)", textDecoration: "line-through" }}>
							{amountText}
						</Typography>
					) : priceLabel ? (
						<Typography style={{ fontSize: 14, color: "#4A6157", fontWeight: 600, textAlign: "right" }}>
							{priceLabel}
						</Typography>
					) : (totalServices > 1) ? (
						<>
							{Utils?.startsAtPricingTypeNames?.includes(pricingType) ? (
								<div
									translate="no"
									className={classes.servicePrice}
									style={{
										display: "flex",
										alignItems: "center",
										flexDirection: "column",
										textAlign: "right",
										fontWeight: "normal",
									}}>
									{amount && amount > 0 ? (
										<div style={{ display: "flex", flexDirection: "column" }}>
											<span
												style={{
													fontSize: "18px",
													fontWeight: "normal",
													lineHeight: 1.2,
													color: "rgba(0,0,0,0.7)"
												}}>
												{t("Starts at")}
											</span>
											<span
												style={{
													fontSize: "18px",
													fontWeight: "normal",
													lineHeight: 1.2,
													color: "rgba(0,0,0,0.7)"
												}}>
												{amountText}
											</span>
										</div>
									) : null}
								</div>
							) : (
								<Typography
									translate="no"
									className={classes.servicePrice}
									style={{
										fontSize: "18px",
										fontWeight: "normal",
										whiteSpace:
											Utils.getProfessionalPrice(service.professional, service.service, business.currency, t) ===
											t("Upon consultation")
												? "pre-line"
												: "normal",
										textAlign: "right",
										lineHeight: 1.2,
										color: "rgba(0,0,0,0.7)"
									}}>
									{Utils.getProfessionalPrice(service.professional, service.service, business.currency, t) ===
									t("Upon consultation")
										? t("Upon\nconsultation")
										: Utils.getProfessionalPrice(service.professional, service.service, business.currency, t)}
								</Typography>
							)}
						</>
					) : (
						<>{checkoutInformation && service.professional && amountText && !shouldStartAt && <>{amountText}</>}</>
					)}
				</div>
				) :
				  <div style={{ width: "20%"}}>
				  	<Typography style={{ width: "100%", textAlign: "center" }}>
						{t("Paid with package")}
					</Typography>
				  </div>
				}
				
			</div>
			{isExpired && expiredCardProps && <ServiceExpiredCard {...expiredCardProps} />}
			{showDivider && <Divider style={{ marginTop: 10, marginBottom: 10 }} />}
		</div>
	);
};

export default ServiceItem;
